import type { ReactNode } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type AuthCardProps = {
  error?: string;
  children: ReactNode;
};

export const authPrimaryButtonClassName = "w-full h-12 text-base font-semibold bg-emerald-600 hover:bg-emerald-700 transition-all active:scale-[0.985]";
export const authLinkClassName = "text-sm text-slate-600 hover:text-emerald-600 transition-colors";

export function AuthCard({ error, children }: AuthCardProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white pb-8 pt-10 text-center relative">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-md">
              <img
                src="/img/logo-login.png"
                alt="BioGain Logo"
                className="h-16 w-16 object-contain"
                width={64}
                height={64}
              />
            </div>
            <CardTitle className="text-4xl font-bold tracking-tight">BioGain™</CardTitle>
            <CardDescription className="text-white/80 text-base mt-2 max-w-xs mx-auto">
              Making land management easier, more effective and sustainable
            </CardDescription>

            <div className="absolute -bottom-1 left-0 right-0 h-4 bg-gradient-to-b from-transparent to-white" />
          </CardHeader>

          <CardContent className="p-8 space-y-6">
            {error && (
              <Alert variant="destructive" className="border-red-200 bg-red-50">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {children}

            <div className="flex items-center justify-center gap-6 pt-4 text-sm text-slate-500">
              <a
                href="https://www.biogain.ai/english-privacy-policy"
                target="_blank"
                className="hover:text-slate-700 transition-colors"
              >
                Privacy
              </a>
              <a
                href="https://www.biogain.ai/terms-of-use"
                target="_blank"
                className="hover:text-slate-700 transition-colors"
              >
                Terms
              </a>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} BioGain. All rights reserved.
        </p>
      </div>
    </div>
  );
}